<template>
  <v-dialog v-model="open" scrollable persistent>
    <v-card v-if="open">
      <v-card-title class="px-4">
        <v-text-field
          v-model="search_term"
          v-debounce:1s="search"
          class="pb-1"
          label="Buscar orçamento"
          outlined
          hide-details
          clearable
        ></v-text-field>
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text class="pa-0">
        <div v-if="loading" style="overflow: hidden; height: 90px; text-align: center; padding-top: 15px">
          <v-progress-circular class="mt-3" color="#777" indeterminate></v-progress-circular>
        </div>
        <div v-if="!loading && list.length === 0" style="overflow: hidden; height: 90px; text-align: center; margin-top: 5px; margin-bottom: 20px">
          <v-icon class="mt-5 mb-1" size="40"> mdi-magnify </v-icon><br />
          <span>Utilize o campo acima para pesquisar</span>
        </div>
        <div class="list-container">
          <div v-for="(budget, index) in list" :key="index" class="item" @click="handleSelect(budget)">
            <div v-if="budget.vehicle" class="vehicle-plate">
              <img :src="budget.vehicle.vehicle_plate_photo_url" />
            </div>
            <div class="client-info">
              <div class="client-name">{{ budget.client ? budget.client.name : 'Cliente não informado' }}</div>
              <div class="client-vehicle">{{ budget.vehicle ? budget.vehicle.description : 'Veículo não informado' }}</div>
            </div>
            <div class="budget-info">
              <div class="status">{{ getBudgetStatus(budget.status).text }}</div>
              <div class="date-price">
                {{ budget.date ? formatDate(budget.date) : 'Sem data' }} · {{ calculateTotal(budget) }} ·
                {{ calculateEstimate(budget) }}
              </div>
            </div>
          </div>
        </div>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions>
        <v-btn text @click="closeDialog()"> Fechar </v-btn>
        <v-spacer></v-spacer>
        <v-btn color="primary" elevation="0" @click="handleCreateBudget"> Novo orçamento </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import BudgetService from '@/services/api/budget';

export default {
  emits: ['close-dialog', 'close-and-create-budget'],
  props: {
    open: Boolean
  },
  data() {
    return {
      loading: false,
      search_term: null,
      list: [],
      selected: null,
      budget_status: [
        { value: 'pendente', text: 'Pendente' },
        { value: 'aguardando_aprovacao', text: 'Aguardando aprovação' },
        { value: 'aguardando_pagamento', text: 'Aguardando pagamento' },
        { value: 'em_andamento', text: 'Em andamento' },
        { value: 'aprovado', text: 'Aprovado' },
        { value: 'cancelado', text: 'Cancelado' },
        { value: 'recusado', text: 'Recusado' }
      ]
    };
  },
  computed: {
    service_order() {
      return this.$store.getters['ServiceOrder/intialDetails'];
    }
  },
  methods: {
    closeDialog() {
      const selected = this.list.find((responsible) => responsible.id === this.selected);
      this.$emit('close-dialog', {
        entity: 'responsible',
        data: selected
      });
    },
    async search() {
      const self = this;

      const queryParams = {
        description: self.search_term,
        size: 10,
        page: 1
      };

      self.loading = true;

      try {
        const response = await BudgetService.search(queryParams);
        self.list = response.data.rows;
      } finally {
        self.loading = false;
      }
    },
    handleSelect(budget) {
      const self = this;
      self.$store.dispatch('ServiceOrder/addBudget', {
        budget,
        user: self.$store.getters['Auth/user'],
        service_order: self.service_order
      });
      this.$emit('close-dialog');
    },
    getBudgetStatus(statusRef) {
      return this.budget_status.find((status) => status.value === statusRef);
    },
    formatDate(date) {
      return new Date(date).toLocaleDateString();
    },
    calculateEstimate(budget) {
      if (budget?.total_estimate > 0) {
        const estimate = budget.total_estimate;

        if (estimate < 60) {
          return `${estimate} minutos`;
        }

        const hours = Math.floor(estimate / 60);
        const minutes = estimate % 60;

        return `${hours}h${minutes.toString().padStart(2, '0')}m`;
      }

      return 'Sem estimativa';
    },
    calculateDiscount(budget) {
      const discountType = budget.discount_type;
      const { subtotal, discount } = budget;

      if (discount) {
        if (discountType === 'percentual') {
          let discountedAmount;

          if (typeof discount === 'string') {
            const cleanedDiscount = discount.replace(/[^\d,.-]/g, '');
            const parsedDiscount = parseFloat(cleanedDiscount.replace(',', '.'));

            discountedAmount = subtotal * (parsedDiscount / 100);
          } else {
            discountedAmount = subtotal * (discount / 100);
          }

          return this.formatCurrency(discountedAmount);
        }

        return this.formatCurrency(discount);
      }

      return this.formatCurrency(0);
    },
    calculateTotal(budget) {
      const subTotal = budget.subtotal;
      const discount = this.calculateDiscount(budget);

      return this.formatCurrency(subTotal - this.parseCurrency(discount));
    },
    parseCurrency(value) {
      return value ? parseFloat(value.replace('R$', '').replace('.', '').replace(',', '.').trim()) : 0;
    },
    formatCurrency(amount) {
      return amount ? parseFloat(amount).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : 'R$ 0,00';
    },
    handleCreateBudget() {
      BudgetService.create({
        service_order_id: this.service_order.id,
        client_id: this.service_order.client?.id,
        vehicle_id: this.service_order.vehicle?.id,
      })
        .then((response) => {
          this.$store.commit('Budget/SET_SELECTED_BUDGET', response.data.budget);

          this.$store.commit('Budget/ADD_LOG', {
            description: 'Orçamento criado',
            createdAt: new Date().toISOString(),
            user: this.$store.getters['Auth/user']
          });

          this.$emit('close-and-create-budget', response.data.budget);
        })
        .catch((error) => {
          console.error(error);
        });
    }
  },
  watch: {
    search_term: {
      handler(term) {
        if (!term || term.length === 0) {
          this.list = [];
        }
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.list-container {
  height: -webkit-fill-available;
  width: -webkit-fill-available;

  .item {
    border-bottom: 3px solid #e0e0e0;
    padding: 10px 20px;
    overflow: hidden;

    .vehicle-plate {
      float: right;
      padding: 10px 8px;
      background-color: #eee;
      overflow: hidden;
      border-radius: 4px;
      width: 90px;

      img {
        float: left;
        width: 100%;
      }

      span {
        float: left;
        font-size: 12px;
        overflow: hidden;
        width: 100%;
        line-height: 19px;
        text-align: center;
        background-color: #eeeeee;
        border-bottom-right-radius: 5px;
        border-bottom-left-radius: 5px;
      }
    }

    .budget-info {
      border-top: 1px solid #e9edef;
      font-size: 12px;
      color: #667781;
      margin-top: 8px;
      overflow: hidden;
      padding-bottom: 0px !important;
      width: 100%;
      float: left;
      padding: 5px 3px;

      .status {
        float: right;
      }
    }

    .client-info {
      float: left;
      padding-top: 1px;

      .client-name {
        font-size: 17px;
        font-weight: 400;
        color: #111b21;
      }

      .client-vehicle {
        font-weight: 400;
        font-size: 13px;
        color: #667781;
      }
    }
  }
}
</style>
